/**
 * Email Suppression List Management
 * Manage bounced, complained, and manually suppressed emails
 *
 * Created: January 3, 2026
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Badge, Card } from "react-bootstrap";
import Modal from "react-modal";
import { toast } from "react-toastify";

import { selectUser } from "../../../components/auth/auth.selector";
import HelmetWrapper from "../../../HelmetWrapper";
import CmnFilters from "../../../components/lead-management/common/CmnFilters";
import ModalHeadAndClose from "../../../components/payouts-wtf/common/ModalHeadAndClose";
import ReactVortoxSpinner from "../../../components/common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../../components/payment-link-system/PaymentLinkTable";

import { fetchSuppressionListData } from "../../../redux/email-broadcast/emailBroadcastSlice";
import {
  addToSuppressionList,
  removeFromSuppressionList,
} from "../../../redux/email-broadcast/emailBroadcastApi";
import { formatDateTimezon } from "../../../components/common-data/Converts";

// Add to Suppression Form
const AddSuppressionForm = ({ closeModal, userInfoData, callFunction }) => {
  const [formData, setFormData] = useState({
    email: "",
    reason: "manual",
    notes: "",
  });
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkEmails, setBulkEmails] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      if (bulkMode) {
        const emails = bulkEmails
          .split(/[\n,]/)
          .map((e) => e.trim().toLowerCase())
          .filter((e) => e && e.includes("@"));

        if (emails.length === 0) {
          toast.error("No valid emails found");
          setLoading(false);
          return;
        }

        let successCount = 0;
        for (const email of emails) {
          const response = await addToSuppressionList(
            { email, reason: formData.reason, notes: formData.notes },
            userInfoData?.token
          );
          if (response?.status) successCount++;
        }

        toast.success(`Added ${successCount} of ${emails.length} emails to suppression list`);
        callFunction();
        closeModal();
      } else {
        if (!formData.email.trim()) {
          toast.error("Email is required");
          setLoading(false);
          return;
        }

        const response = await addToSuppressionList(formData, userInfoData?.token);
        if (response?.status) {
          toast.success("Email added to suppression list");
          callFunction();
          closeModal();
        } else {
          toast.error(response?.message || "Failed to add email");
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to add email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-3">
      <div className="mb-3">
        <div className="form-check form-check-inline">
          <input
            type="radio"
            className="form-check-input"
            id="single"
            checked={!bulkMode}
            onChange={() => setBulkMode(false)}
          />
          <label className="form-check-label" htmlFor="single">
            Single Email
          </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            type="radio"
            className="form-check-input"
            id="bulk"
            checked={bulkMode}
            onChange={() => setBulkMode(true)}
          />
          <label className="form-check-label" htmlFor="bulk">
            Bulk Import
          </label>
        </div>
      </div>

      {bulkMode ? (
        <div className="mb-3">
          <label className="form-label">Email Addresses</label>
          <textarea
            className="form-control font-monospace"
            rows={8}
            value={bulkEmails}
            onChange={(e) => setBulkEmails(e.target.value)}
            placeholder="Enter one email per line or comma-separated:&#10;email1@example.com&#10;email2@example.com&#10;email3@example.com"
          />
          <small className="text-muted">
            One email per line or comma-separated. Invalid emails will be skipped.
          </small>
        </div>
      ) : (
        <div className="mb-3">
          <label className="form-label">Email Address *</label>
          <input
            type="email"
            className="form-control"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="email@example.com"
            required={!bulkMode}
          />
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">Reason</label>
        <select
          className="form-select"
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
        >
          <option value="manual">Manual suppression</option>
          <option value="bounce">Hard bounce</option>
          <option value="complaint">Spam complaint</option>
          <option value="unsubscribe">Unsubscribe request</option>
          <option value="invalid">Invalid email</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label">Notes (optional)</label>
        <textarea
          className="form-control"
          rows={2}
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Optional notes about why this email is suppressed..."
        />
      </div>

      <div className="d-flex gap-2">
        <button type="submit" className="btn btn-danger flex-grow-1" disabled={loading}>
          {loading ? "Adding..." : "Add to Suppression List"}
        </button>
        <button type="button" className="btn btn-secondary" onClick={closeModal}>
          Cancel
        </button>
      </div>
    </form>
  );
};

const EmailSuppression = () => {
  const dispatch = useDispatch();
  const userInfoData = useSelector(selectUser);
  const { suppressionList, suppressionListLoading } = useSelector(
    (state) => state.emailBroadcast || {}
  );

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);

  const iniAllFilter = { searchString: "", reason: "" };
  const [allFilters, setAllFilters] = useState(iniAllFilter);
  const [filters, setFilters] = useState({ page: 1, limit: 20 });
  const [searchFilterApplied, setSearchFilterApplied] = useState(false);
  const [selectedReason, setSelectedReason] = useState("all");

  const iniModValues = { isModOpen: false, modName: "" };
  const [modValues, setModValues] = useState(iniModValues);

  const fetchSuppression = (filters) => {
    dispatch(fetchSuppressionListData({ filters, token: userInfoData?.token }));
  };

  useEffect(() => {
    fetchSuppression(filters);
  }, [filters]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page: isPage + 1, limit: pageLimit }));
  }, [isPage, pageLimit]);

  useEffect(() => {
    if (selectedReason !== "all") {
      setFilters((prev) => ({ ...prev, reason: selectedReason, page: 1 }));
    } else {
      const { reason, ...rest } = filters;
      setFilters({ ...rest, page: 1 });
    }
  }, [selectedReason]);

  const onClickSearch = () => {
    setIsPage(0);
    if (allFilters.searchString) {
      setSearchFilterApplied(true);
      setFilters((prev) => ({ ...prev, search: allFilters.searchString }));
    }
  };

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
    setSelectedReason("all");
    setFilters({ page: 1, limit: 20 });
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove "${email}" from suppression list? This will allow emails to be sent to this address again.`)) {
      return;
    }

    try {
      const response = await removeFromSuppressionList(email, userInfoData?.token);
      if (response?.status) {
        toast.success("Email removed from suppression list");
        fetchSuppression(filters);
      } else {
        toast.error(response?.message || "Failed to remove email");
      }
    } catch (error) {
      toast.error("Failed to remove email");
    }
  };

  const getReasonBadge = (reason) => {
    const colors = {
      bounce: "danger",
      hard_bounce: "danger",
      soft_bounce: "warning",
      complaint: "danger",
      unsubscribe: "secondary",
      manual: "info",
      invalid: "dark",
      other: "secondary",
    };
    const labels = {
      bounce: "Bounced",
      hard_bounce: "Hard Bounce",
      soft_bounce: "Soft Bounce",
      complaint: "Complaint",
      unsubscribe: "Unsubscribed",
      manual: "Manual",
      invalid: "Invalid",
      other: "Other",
    };
    return <Badge bg={colors[reason] || "secondary"}>{labels[reason] || reason}</Badge>;
  };

  const tbColumns = [
    {
      dataField: "email",
      text: "Email",
      formatter: (_, row) => <span className="fw-medium">{row.email}</span>,
    },
    {
      dataField: "reason",
      text: "Reason",
      formatter: (_, row) => getReasonBadge(row.reason),
    },
    {
      dataField: "source",
      text: "Source",
      formatter: (_, row) => row.source || "System",
    },
    {
      dataField: "notes",
      text: "Notes",
      formatter: (_, row) => (
        <small className="text-muted">{row.notes || "-"}</small>
      ),
    },
    {
      dataField: "campaign_name",
      text: "Campaign",
      formatter: (_, row) => row.campaign_name || "-",
    },
    {
      dataField: "date_added",
      text: "Added",
      formatter: (_, row) => (
        <small>{formatDateTimezon(row.date_added) || "N/A"}</small>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <button
          className="btn btn-sm btn-outline-success"
          onClick={(e) => {
            e.stopPropagation();
            handleRemove(row.email);
          }}
        >
          Remove
        </button>
      ),
    },
  ];

  // Calculate stats
  const stats = {
    total: suppressionList?.pagination?.totalRecords || 0,
    bounces: suppressionList?.data?.filter((s) => s.reason?.includes("bounce")).length || 0,
    complaints: suppressionList?.data?.filter((s) => s.reason === "complaint").length || 0,
    unsubscribes: suppressionList?.data?.filter((s) => s.reason === "unsubscribe").length || 0,
    manual: suppressionList?.data?.filter((s) => s.reason === "manual").length || 0,
  };

  return (
    <HelmetWrapper title="WTF: Suppression List">
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        <Modal
          isOpen={modValues.isModOpen}
          onRequestClose={() => setModValues(iniModValues)}
          contentLabel="Suppression Modal"
          ariaHideApp={false}
        >
          <div className="createNewCTOModalContainer mod-width-50p">
            <ModalHeadAndClose
              title="Add to Suppression List"
              onClose={() => setModValues(iniModValues)}
            />
            <AddSuppressionForm
              closeModal={() => setModValues(iniModValues)}
              userInfoData={userInfoData}
              callFunction={() => fetchSuppression(filters)}
            />
          </div>
        </Modal>

        {/* Header */}
        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">Suppression List</h5>
                <small className="text-muted">
                  Emails that will never receive campaigns
                </small>
              </div>
              <button
                className="btn btn-danger"
                onClick={() => setModValues({ isModOpen: true, modName: "add" })}
              >
                + Add Email
              </button>
            </div>
          </Col>
        </Row>

        {/* Stats */}
        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Body className="py-2">
                <Row className="text-center">
                  <Col>
                    <h5 className="mb-0">{stats.total.toLocaleString()}</h5>
                    <small className="text-muted">Total Suppressed</small>
                  </Col>
                  <Col>
                    <h5 className="mb-0 text-danger">{stats.bounces}</h5>
                    <small className="text-muted">Bounces</small>
                  </Col>
                  <Col>
                    <h5 className="mb-0 text-warning">{stats.complaints}</h5>
                    <small className="text-muted">Complaints</small>
                  </Col>
                  <Col>
                    <h5 className="mb-0 text-secondary">{stats.unsubscribes}</h5>
                    <small className="text-muted">Unsubscribes</small>
                  </Col>
                  <Col>
                    <h5 className="mb-0 text-info">{stats.manual}</h5>
                    <small className="text-muted">Manual</small>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Reason Filter */}
        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Body className="py-2">
                <div className="d-flex gap-2">
                  {[
                    { key: "all", label: "All" },
                    { key: "bounce", label: "Bounces" },
                    { key: "complaint", label: "Complaints" },
                    { key: "unsubscribe", label: "Unsubscribes" },
                    { key: "manual", label: "Manual" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      className={`btn btn-sm ${selectedReason === tab.key ? "btn-danger" : "btn-outline-secondary"}`}
                      onClick={() => setSelectedReason(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <CmnFilters
              from="suppression-list"
              userInfoData={userInfoData}
              searchFilterApplied={searchFilterApplied}
              filters={allFilters}
              setFilters={setAllFilters}
              onClickSearch={onClickSearch}
              onClickReset={onResetFilters}
              isLoading={suppressionListLoading}
              searchPlaceHolder="Search by email..."
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt15">
              {suppressionListLoading ? (
                <ReactVortoxSpinner />
              ) : (
                <PaymentLinkTable
                  tableData={suppressionList?.data}
                  tableColumns={tbColumns}
                  tablePagination={suppressionList?.pagination}
                  isPage={isPage}
                  setIsPage={setIsPage}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </Col>
        </Row>

        {/* Info Card */}
        <Row className="mt-4">
          <Col>
            <Card className="bg-light">
              <Card.Body>
                <h6>About Suppression List</h6>
                <ul className="mb-0 small">
                  <li>
                    <strong>Bounces:</strong> Automatically added when emails bounce (hard bounces are permanent)
                  </li>
                  <li>
                    <strong>Complaints:</strong> Added when recipients mark emails as spam
                  </li>
                  <li>
                    <strong>Unsubscribes:</strong> Added when users click unsubscribe links
                  </li>
                  <li>
                    <strong>Manual:</strong> Emails you've manually added to prevent sending
                  </li>
                  <li className="text-danger">
                    <strong>Important:</strong> Removing emails from suppression can harm your sender reputation. Only remove if you're certain the email is valid and the user wants to receive emails.
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default EmailSuppression;
